import { useTranslation } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import SlickGrid1, {
  typedColumns,
  type ColumnDefinition,
  type ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import UpdateIcon from "@mui/icons-material/Update";
import EditIcon from "@Assets/icons/edit.svg?react";
import { saveResourceIds } from "@Actions/resource";
import CloseIcon from "@mui/icons-material/Close";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import { useSelector } from "react-redux";
import { GanttStatic, ResourceAssignment, Task } from "./dhtmlxgantt";
import { UpdatesModalProps } from "../interface";
import useGanttHelper, { resolveTaskResources, toAvailability } from "./useGanttHelper";
import { RootState } from "@Reducers/index";
import type { SaveResourceIdsInTaskRequest } from "@ServerTypes/resources/resources.schema";
import { objectIdString } from "@Helpers/schemas/common.schema";
import type { ScheduleGridRef } from "../interface";
declare const gantt: GanttStatic;

/** The four task date fields this modal renders a picker for. */
type TaskDateField = "start_date" | "end_date" | "actualStartDate" | "actualEndDate";

function UpdatesModal(props: Readonly<UpdatesModalProps>) {
  const {
    currentSelectedTask,
    setCurrentSelectedTask,
    setShowProgressModal,
    showProgressModal,
    scheduleId,
    loadingProgressModal,
    setLoadingProgressModal,
    ganttConfig,
  } = props;
  const { t } = useTranslation();
  const [slickGridEditMode, setSlickGridEditMode] = useState(false);
  const { getHoursPerDay, HOUR } = useGanttHelper();
  const [actualResourceWork, setActualResourceWork] = useState<string | null>(null);
  // Buffered edits are sent verbatim as `owner[]` on /resources/saveResourceIdsInTask.
  const [slickGridEditData, setSlickGridEditData] = useState<SaveResourceIdsInTaskRequest["owner"]>([]);
  const [slickGridDataset, setSlickGridDataset] = useState<ResourceAssignment[]>([]);
  const [editButtonDisabled, setEditButtonDisabled] = useState(true);
  const [showGrid] = useState(false);
  const slickGridRef = useRef<ScheduleGridRef<ResourceAssignment> | null>(null);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const currencySymbol = useSelector((state: RootState) => state.currencyDetails.symbolDetail?.name) ?? "";

  /**
   * The chart's own record for the task being edited. `currentSelectedTask` is a
   * `Partial<GanttTask>` whose `id` is optional, so it is not a dhtmlx `Task`; helpers that need a
   * real one read it back from the chart, as `handleConfirmClick` and the effect below do.
   */
  const getSelectedGanttTask = (): Task => gantt.getTask(gantt.getSelectedId());

  const plannedWorksTemplate = (
    _row: number,
    _cell: number,
    _value: unknown,
    _col: ColumnDefinition<ResourceAssignment, string>,
    args: ResourceAssignment,
  ) => {
    if (args.availability) {
      const selectedTask: Task = gantt.getTask(gantt.getSelectedId());
      return ((args.availability * (selectedTask.duration || 0)) / 100) * 8;
    } else {
      return 0;
    }
  };

  const actualWorkTemplate = () => {
    // An editor template only renders for the row being edited, so the handle, the index and the row
    // all exist. Erased assertions.
    const selectedRow = slickGridRef.current!.slickGrid.selectedRows[0]!;
    const selectedRowdData = slickGridRef.current!.dataView.items[selectedRow]!;
    return (
      <div>
        <TextFieldComponent
          onChange={(e) => {
            setActualResourceWork(e.target.value);
          }}
          defaultValue={selectedRowdData.actualResourceWork}
          type="number"
        />
      </div>
    );
  };
  const updateProgressSlickgridColumns = useRef<ValidatedColumnDefinition<ResourceAssignment>[]>(
    typedColumns<ResourceAssignment>([
      {
        field: "counter",
        id: "counter",
        width: 40,
        textAlign: "Left",
        sortable: true,
        name: t("ID"),
      },
      {
        field: "text",
        id: "text",
        width: 50,
        textAlign: "Left",
        sortable: true,
        name: t("Resource Name"),
      },
      {
        field: "plannedWorks",
        id: "plannedWorks",
        width: 10,
        textAlign: "Left",
        sortable: true,
        name: t("Planned Works(in hrs)"),
        formatter: plannedWorksTemplate,
      },
      {
        field: "actualResourceWork",
        id: "actualResourceWork",
        width: 20,
        textAlign: "Left",
        sortable: true,
        name: t("Actual Work (in hrs)"),
        editorTemplate: actualWorkTemplate,
      },
    ]),
  );

  useEffect(() => {
    const selectedTask = gantt.getTask(gantt.getSelectedId());
    const assignedResources = resolveTaskResources(selectedTask);
    if (assignedResources.length) {
      setSlickGridDataset(assignedResources.filter((resource: ResourceAssignment) => resource.type != "material"));
    }
  }, []);

  const handleUpdateClick = () => {
    // "Update" is only enabled with exactly one row selected. Erased assertions.
    const selectedRow = slickGridRef.current!.slickGrid.selectedRows[0]!;
    const selectedRowdData = slickGridRef.current!.dataView.items[selectedRow]!;
    selectedRowdData.actualResourceWork = actualResourceWork;
    // SHAPE GAP: DHTMLX types an assignment's `resource_id` as `string | number`, while
    // /resources/saveResourceIdsInTask requires a 24-hex id, so it is parsed rather than asserted.
    const resourceId = objectIdString.safeParse(selectedRowdData.resource_id);
    if (resourceId.success) {
      const updatedObj = {
        counter: selectedRowdData.counter,
        id: selectedRowdData.id,
        resource_id: resourceId.data,
        text: selectedRowdData.text,
        availability: toAvailability(selectedRowdData.availability),
        cost: selectedRowdData.cost,
        actualResourceWork: actualResourceWork,
        type: selectedRowdData.type,
      };
      // One buffered entry per resource; update whatever is already buffered for it, else append.
      const buffered = slickGridEditData.filter((data) => updatedObj.resource_id == data.resource_id);
      buffered.forEach((data) => {
        data.actualResourceWork = actualResourceWork;
      });
      if (!buffered.length) {
        setSlickGridEditData((prev) => [...prev, updatedObj]);
      }
    }
    slickGridRef.current!.dataView.items[selectedRow] = selectedRowdData;
    slickGridRef.current!.dataView.setItems(slickGridRef.current!.dataView.items);
    setSlickGridEditMode(false);
    setActualResourceWork(null);
    setEditButtonDisabled(false);
  };

  const handleEditClick = () => {
    const selectedRow = slickGridRef.current!.slickGrid.selectedRows[0]!;
    const selectedRowdData = slickGridRef.current!.dataView.items[selectedRow]!;
    setSlickGridEditMode(true);
    setEditButtonDisabled(true);
    setActualResourceWork(selectedRowdData.actualResourceWork);
  };

  const handleProgressUpdateChanges = (field: string, value: string | number | null) => {
    const selectedTask = gantt.getTask(currentSelectedTask.id!);
    const tempFields = { ...currentSelectedTask };
    tempFields[field] = value;
    if (field === "progress" || field === "actualCost") {
      tempFields[field] = Number(Number(value).toFixed(2));
    }
    if (tempFields.actualStartDate && tempFields.actualEndDate) {
      tempFields.actualDuration = gantt.calculateDuration({
        start_date: new Date(tempFields.actualStartDate),
        end_date: new Date(tempFields.actualEndDate),
        task: selectedTask,
      });
      tempFields.progress = 100;
    }
    setCurrentSelectedTask(tempFields);
  };

  const getDateComponent = (field: TaskDateField) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          timezone={userDetails.timeZone}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              sx: {
                "& .MuiInputBase-root": {
                  height: "2rem",
                  backgroundColor: field == "end_date" || field == "start_date" ? "var(--light-blue-tag)" : "",
                },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                  border: field == "end_date" || field == "start_date" ? "none !important" : "",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#666A84",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6BBBE3",
                  borderWidth: "0.0625rem",
                },
              },
            },
          }}
          // id={`${field}dateTimePicker`}
          format="MMM D YYYY, hh:mm A"
          value={currentSelectedTask[field] ? dayjs(currentSelectedTask[field]) : null}
          onChange={(newValue) => {
            handleProgressUpdateChanges(field, newValue ? newValue.toDate().getTime() : null);
          }}
          disabled={field == "end_date" || field == "start_date"}
        />
      </LocalizationProvider>
    );
  };

  const handleRowSelection = () => {
    if (slickGridRef.current) {
      const selectedRows = slickGridRef.current.slickGrid.selectedRows;
      setEditButtonDisabled(selectedRows.length !== 1);
      if (selectedRows.length != 1) {
        setSlickGridEditMode(false);
      }
    }
  };

  const handleCancelClick = () => {
    setSlickGridEditMode(false);
    slickGridRef.current!.slickGrid.setSelectedRows([]);
    setEditButtonDisabled(true);
  };

  const updateProgessModalContent = () => {
    return (
      <div className="mt-6 w-full px-8">
        <div className="grid grid-cols-[7.6875rem,1fr,7.6875rem,1fr] gap-x-6 gap-y-6 items-center">
          <Text className="flex items-center h-4 text-base font-semibold">{t("ID")}</Text>
          <TextFieldComponent
            value={currentSelectedTask.TaskDisp}
            variant="outlined"
            slotProps={{
              htmlInput: {
                readOnly: true,
              },
            }}
            disabled
          />

          <Text className="flex items-center h-4 text-base font-semibold">{t("Name")}</Text>
          <TextFieldComponent
            value={currentSelectedTask.text}
            variant="outlined"
            slotProps={{
              htmlInput: {
                readOnly: true,
              },
            }}
            disabled
          />

          <Text className="flex items-center h-4 text-base font-semibold">{t("Start Date")}</Text>
          {getDateComponent("start_date")}

          <Text className="flex items-center h-4 text-base font-semibold">{t("Finish Date")}</Text>
          {getDateComponent("end_date")}

          <Text className="flex items-center h-4 text-base font-semibold">{t("Actual Start Date")}</Text>
          {getDateComponent("actualStartDate")}

          <Text className="flex items-center h-4 text-base font-semibold">{t("Actual Finish Date")}</Text>
          {getDateComponent("actualEndDate")}

          <Text className="flex items-center h-4 text-base font-semibold">{t("Actual Duration")}</Text>
          <TextFieldComponent
            value={
              ganttConfig!.current == HOUR
                ? Number(currentSelectedTask.actualDuration) / getHoursPerDay(getSelectedGanttTask())
                : currentSelectedTask.actualDuration
            }
            disabled
          />

          <Text className="flex items-center h-4 text-base font-semibold text-primary_text">{t("Progress (%)")}</Text>
          <TextFieldComponent
            value={Number(currentSelectedTask.progress!.toFixed(2))}
            onChange={(e) => {
              const val = e.target.value;
              handleProgressUpdateChanges("progress", val);
            }}
            disabled={!currentSelectedTask.actualStartDate}
            type="number"
          />

          <Text className="flex items-center h-4 text-base font-semibold">
            {currencySymbol ? `${t("Actual Cost")} (${currencySymbol})` : t("Actual Cost")}
          </Text>
          <TextFieldComponent
            value={currentSelectedTask.actualCost}
            onChange={(e) => {
              const val = e.target.value;
              handleProgressUpdateChanges("actualCost", val);
            }}
            disabled={!currentSelectedTask.actualStartDate}
            type="number"
          />
        </div>

        {showGrid && (
          <div className="flex flex-col w-full h-80 mt-6">
            <Text className="mb-2 text-sm font-semibold">{t("Actual Work Complete")}</Text>
            <div className="flex p-2 gap-2 ">
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                onClick={handleEditClick}
                disabled={editButtonDisabled || !currentSelectedTask.actualStartDate}
              >
                <div
                  className={`flex items-center gap-1 ${
                    editButtonDisabled || !currentSelectedTask.actualStartDate ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <EditIcon />
                  <Text>{t("Edit")}</Text>
                </div>
              </ButtonComponent>

              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                onClick={() => {
                  handleUpdateClick();
                }}
                disabled={!slickGridEditMode}
              >
                <div className="flex items-center gap-1">
                  <UpdateIcon fontSize="small" />
                  <Text>{t("Update")}</Text>
                </div>
              </ButtonComponent>
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                onClick={handleCancelClick}
                disabled={!slickGridEditMode}
              >
                <div className="flex items-center gap-1">
                  <CloseIcon fontSize="small" />
                  <Text>{t("Cancel")}</Text>
                </div>
              </ButtonComponent>
            </div>
            <SlickGrid1
              ref={slickGridRef}
              columnDefinitions={updateProgressSlickgridColumns.current}
              dataset={slickGridDataset}
              isEditMode={slickGridEditMode}
              onSelectedRowIdsChanged={handleRowSelection}
              hideNoDataIllustration
            />
          </div>
        )}
      </div>
    );
  };

  const handleConfirmClick = async () => {
    const selectedTask = gantt.getTask(gantt.getSelectedId());
    selectedTask.actualStartDate = currentSelectedTask.actualStartDate
      ? new Date(currentSelectedTask.actualStartDate)
      : null;
    selectedTask.actualEndDate = currentSelectedTask.actualEndDate ? new Date(currentSelectedTask.actualEndDate) : null;
    selectedTask.progress = currentSelectedTask.progress ? currentSelectedTask.progress / 100 : 0;
    selectedTask.actualCost = currentSelectedTask.actualCost ?? 0;
    gantt.updateTask(currentSelectedTask.id!);
    const taskId = objectIdString.safeParse(currentSelectedTask.id);
    if (slickGridEditData.length && taskId.success && scheduleId) {
      await saveResourceIds({
        taskId: taskId.data,
        scheduleId,
        owner: slickGridEditData,
      });
    }
    setLoadingProgressModal(true);
  };
  const updateProgressFooterContent = () => {
    return (
      <div className="flex px-8 py-4 gap-4" style={{ borderTopColor: "#ECF3FE", borderTopWidth: "0.0625rem" }}>
        <ButtonComponent
          loading={loadingProgressModal}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleConfirmClick}
        >
          <Text>{t("Confirm")}</Text>
        </ButtonComponent>
        <ButtonComponent
          buttonType={BUTTON_TYPES.BORDER_BUTTON}
          onClick={() => {
            setShowProgressModal(false);
            slickGridRef.current = null;
          }}
        >
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };

  return (
    <div>
      <ModalStateDialogComponent
        modalPadding={0}
        onCancel={() => {
          setShowProgressModal(false);
          slickGridRef.current = null;
        }}
        headerTitle={<Text className="h-16 w-full justify-center px-8 py-5 font-bold">{t("Update Progress")}</Text>}
        modelContent={updateProgessModalContent()}
        footerContent={updateProgressFooterContent()}
        isVisible={showProgressModal}
        width="80%"
        height="70%"
      />
    </div>
  );
}

export default UpdatesModal;
